import { ReactNode, MouseEvent } from "react";
import classNames from "classnames";
import Link from "next/link";

interface ButtonProps {
  className?: string;
  variant?: "primary" | "secondary" | "tertiary" | "primary-warning";
  href?: string;
  onClick?: (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  icon?: ReactNode;
  isLoading?: boolean;
  loadingText?: string;
  target?: string;
  rel?: string;
  size?: "large";
  children: ReactNode;
  disabled?: boolean;
  type?: "button" | "submit";
}

export default function Button({
  className = "", 
  variant = "primary", 
  href,
  onClick,
  icon, 
  children, 
  isLoading,
  loadingText,
  disabled,
  size,
  type = "button",
  ...props 
}: ButtonProps) {
    
  const baseStyles = classNames(
    "group inline-flex items-center justify-center duration-300 cursor-pointer",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green focus-visible:ring-offset-2 focus-visible:ring-offset-beige dark:focus-visible:ring-offset-black"
  );
  
  const variants = {
    primary: classNames(
      "px-4 py-1.5 rounded-lg border-2",
      "text-[16px] md:text-[18px] xl:text-[20px]",
      "bg-green border-green text-white dark:text-black font-funnel-bold",
      "hover:bg-transparent hover:text-green",
      "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-green disabled:hover:text-beige"
    ),
    "primary-warning": classNames(
      "px-4 py-1.5 rounded-lg border-2",
      "text-[16px] md:text-[18px] xl:text-[20px]",
      "bg-red-500/70 dark:bg-red-500/30 border-red-500 text-white font-funnel-bold",
      "hover:bg-red-500 hover:text-white transition-colors duration-300",
      "disabled:opacity-50 disabled:cursor-not-allowed"
    ),
    secondary: classNames(
      "px-5 py-2 rounded-lg",
      "bg-green/10 text-green-dark dark:text-green font-bold text-[16px]",
      "hover:bg-green/20 [&>span]:transition-transform [&>span]:duration-300 hover:[&>span]:scale-105",
      "disabled:opacity-50 disabled:cursor-not-allowed"
    ),
    tertiary: classNames(
      "font-medium",
      "text-black hover:text-green dark:text-beige",
      {
        "text-[20px] md:text-[22px] xl:text-[24px]": size == "large",
        "text-[16px] md:text-[18px] xl:text-[20px]": size != "large",
      }
    )
  };

  const classes = classNames(baseStyles, variants[variant], className);

  const content = (
    <>
      <span className={classNames(
        "flex items-center",
          {
            "mr-2": icon,
            "relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 hover:after:w-full group-hover:after:w-full after:h-[2px] after:bg-beige hover:after:bg-green group-hover:after:bg-green after:transition-all after:duration-300 after:ease-in-out": href && variant === "tertiary"
          }
        )}
      >
        {children}
      </span>
      {icon && <span className="shrink-0 group-hover:translate-x-1 group-hover:-rotate-6 transition-transform">{icon}</span>}
    </>
  );

  if (href) {
    if (href.startsWith("http") || href.startsWith("mailto") || href.startsWith("tel")) {
      return (
        <a href={href} className={classes} onClick={onClick} {...props as any}>
          {content}
        </a>
      );
    }
    return (
      <Link href={href} className={classes} onClick={onClick} {...props as any}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      className={classes}
      onClick={onClick}
      disabled={isLoading || disabled}
    >
      {content}
    </button>
  );
}